import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ChevronLeft } from "lucide-react";
import SEOMeta from "@/components/SEOMeta";
import ExitIntent from "@/components/ExitIntent";

const STRATEGIES = {
  "iron-condor-nifty": {
    title: "Iron Condor on Nifty — Strategy Guide & Payoff Calculator",
    description: "How to trade Iron Condor on Nifty 50 options. Setup, breakeven calculation, max profit/loss, and when to use this strategy.",
    keywords: "iron condor nifty, iron condor options strategy india, nifty iron condor setup",
    category: "Options — Non-Directional",
    legs: ["Sell OTM Call (higher strike)", "Buy further OTM Call (hedge)", "Sell OTM Put (lower strike)", "Buy further OTM Put (hedge)"],
    maxProfit: "Net premium collected (when Nifty stays between short strikes at expiry)",
    maxLoss: "Spread width minus premium collected (if Nifty moves past either long strike)",
    bestWhen: ["IV Rank above 50 (options are expensive — ideal to sell)", "Market in sideways/consolidation phase", "No major events within the expiry period", "PCR between 0.9 and 1.2 (neutral positioning)"],
    avoid: ["Around budget, RBI meetings, election results", "When IV Rank is below 30 (cheap options = low premium collected)", "Strong trending markets (directional breakouts destroy condors)"],
    body: `An Iron Condor is a four-leg options strategy designed to profit when the underlying stays within a range.

For Nifty, this is one of the most popular strategies among retail traders because:
1. **Defined risk**: Maximum loss is known before entering the trade
2. **Profits from time decay**: Theta works in your favor every day
3. **Non-directional**: You don't need to predict market direction — just that it stays rangebound

## Setting Up a Nifty Iron Condor

**Example setup (Nifty at 19,500):**
- Sell 19,800 CE at ₹45
- Buy 20,000 CE at ₹18
- Sell 19,200 PE at ₹50
- Buy 19,000 PE at ₹22

Net premium collected: ₹45 + ₹50 - ₹18 - ₹22 = **₹55 per lot**
(₹55 × 65 = ₹3,575 per lot of 65 if Nifty stays between 19,200 and 19,800 at expiry)

Max loss per lot: (200 - 55) × 65 = ₹9,425

## Selecting Your Strike Prices

**Short strikes**: Place 1.5–2 standard deviations away from current price. A quick approximation:
- ATM straddle premium × 0.6 = approximate 1SD move expected by expiry
- If ATM straddle is ₹200, expected move is ±₹120 (1SD)
- Place short strikes at ₹120–₹160 away from current price

**Long strikes**: Place 200–250 points beyond short strikes for Nifty weekly expiries. This balances cost of hedge vs. capital freed up.

## Managing the Trade

- **If Nifty stays within range**: Let theta work. Exit at 50–70% of max profit (don't get greedy waiting for full profit — gamma risk increases near expiry)
- **If Nifty approaches short strike**: Roll the tested side out to next week or close the position
- **Stop loss**: Close the entire position if you've lost 2× the premium collected`,
  },
  "long-straddle-banknifty": {
    title: "Long Straddle on BankNifty — Strategy Guide",
    description: "When and how to trade Long Straddle on BankNifty. Best setups, breakeven points, and timing around events.",
    keywords: "long straddle banknifty, banknifty straddle strategy, options straddle india",
    category: "Options — Directional Volatility",
    legs: ["Buy ATM Call", "Buy ATM Put (same strike, same expiry)"],
    maxProfit: "Unlimited (if BankNifty makes a large move in either direction)",
    maxLoss: "Total premium paid (if BankNifty stays exactly at the strike price at expiry)",
    bestWhen: ["IV Rank below 30 (options are cheap)", "Major event approaching (RBI, election, results)", "Market in tight consolidation before expected breakout", "Both technicals and fundamentals suggest volatility is coming"],
    avoid: ["High IV environments (IV Rank > 60) — you pay expensive premiums that decay fast", "After a major event has already passed", "In the last 2 days before expiry (theta decay is brutal)"],
    body: `A Long Straddle profits when the underlying makes a large move in either direction. You buy both a call and put at the same strike — if BankNifty moves enough to cover the combined premium, you profit.

## Why BankNifty for Straddles?

BankNifty moves more than Nifty — it has higher beta and reacts more violently to RBI news, banking sector events, and global financial shocks. This makes it ideal for straddles:

- Higher premium means higher profit potential on big moves
- Weekly expiry (Wednesday) gives you a short time horizon
- Liquidity is excellent — tight bid-ask spreads

## Calculating Your Breakeven

If you buy the 44,000 straddle for:
- 44,000 CE: ₹180
- 44,000 PE: ₹165
- Total cost: ₹345 per lot

**Upper breakeven**: 44,000 + 345 = 44,345
**Lower breakeven**: 44,000 - 345 = 43,655

BankNifty needs to move ±₹345 (about 0.8%) from the current level by expiry for you to break even. Any move larger than this = profit.

## The Event Play — The Best Straddle Setup

The classic straddle setup for Indian traders:

1. RBI MPC meeting announced (quarterly)
2. 2-3 days before: IV starts rising, but straddle is still reasonably priced
3. **Buy straddle 2 days before the announcement** (IV rising but not yet at peak)
4. RBI announces — BankNifty makes a move
5. **Exit same day** — don't hold through the "IV crush" after the announcement

Even if BankNifty moves only 1%, the remaining time value and the move itself often make the position profitable.

## When Straddles Fail

The straddle's enemy is time in a low-volatility environment. If you buy a straddle and BankNifty just drifts sideways for a week, theta decay eats your premium every day.

Rule: Never buy a straddle "just because." There must be a specific reason to expect a large move — an event, a technical squeeze, or extremely low IV (cheap insurance).`,
  },
  "bull-call-spread": {
    title: "Bull Call Spread — Options Strategy Guide for Indian Markets",
    description: "How to set up a Bull Call Spread on Nifty or individual stocks. Lower cost than buying calls outright, with defined risk.",
    keywords: "bull call spread india, call spread options strategy, bull call spread nifty",
    category: "Options — Bullish",
    legs: ["Buy lower strike Call (ATM or slightly OTM)", "Sell higher strike Call (further OTM — reduces cost)"],
    maxProfit: "Difference between strikes minus net premium paid",
    maxLoss: "Net premium paid (limited, known upfront)",
    bestWhen: ["Moderately bullish outlook (not expecting a huge rally, just a measured move up)", "IV is neutral to high (selling the upper call offsets the high IV cost)", "Strong support level nearby for defined downside"],
    avoid: ["Extremely bullish outlook — outright call gives unlimited upside, spread caps it", "Very low IV — both calls are cheap, the spread's advantage is reduced"],
    body: `A Bull Call Spread is a lower-cost alternative to buying a call outright. You buy a call at a lower strike and sell a call at a higher strike — the premium from the sold call reduces your total cost.

## Why Use a Spread Instead of a Naked Call?

**Naked call (buy only)**:
- Cost: ₹150 premium
- Profit if Nifty goes to 20,000: Unlimited upside
- Loss if wrong: ₹150 (full premium)

**Bull Call Spread (buy 19,600 CE, sell 19,900 CE)**:
- Buy 19,600 CE: ₹150
- Sell 19,900 CE: ₹60
- Net cost: ₹90
- Max profit: (300 - 90) × 50 = ₹10,500
- Max loss: ₹90 × 50 = ₹4,500

The spread costs 40% less. Your max profit is capped at the sold strike, but your breakeven is lower and your capital at risk is reduced.

## Choosing Your Strike Prices

**Buy strike**: At-the-money (ATM) or 1 strike OTM. You want some intrinsic value.

**Sell strike**: At your target price. "I think Nifty will reach 19,900 by expiry" → sell the 19,900 CE.

**Spread width**: Typically 200–300 points for Nifty weekly expiries. Wider = more max profit potential, but costs more.

## Exit Strategy

- **Target hit early**: If Nifty reaches your upper strike before expiry, close the spread. You've captured most of the max profit but still have 2-3 days of risk.
- **Stop loss**: Exit if you've lost 50% of the premium paid (net cost drops to ₹45 in the example above).
- **At expiry**: If Nifty is above your long strike, the spread is in profit. If below your long strike, maximum loss is locked in.

## Bull Call Spread vs Bull Put Spread

Both are bullish strategies, but they differ in how you receive money:

- **Bull Call Spread**: You pay a debit (net buyer)
- **Bull Put Spread**: You receive a credit (net seller)

In high IV environments (IV Rank > 60), prefer the Bull Put Spread — you collect more premium. In low IV environments, prefer the Bull Call Spread.`,
  },
  "covered-call-nifty": {
    title: "Covered Call Strategy on Nifty — Generate Monthly Income",
    description: "How to generate regular income from your Nifty holdings using covered calls. Step-by-step setup, strike selection, and roll strategy.",
    keywords: "covered call nifty india, covered call strategy india, generate income options india",
    category: "Options — Income",
    legs: ["Hold Nifty 50 (via ETF, futures, or index fund)", "Sell OTM Call against your holding"],
    maxProfit: "Premium collected + price appreciation up to sold strike",
    maxLoss: "Loss on underlying holding minus premium collected (same as holding without the call)",
    bestWhen: ["You hold Nifty and want to generate additional income", "IV Rank is high (expensive calls = more premium)", "Mildly bullish to neutral outlook (not expecting a big rally)"],
    avoid: ["When you expect a large rally — you cap your upside at the sold strike", "When IV is very low — premium collected is too small to justify the capped upside"],
    body: `A Covered Call is the safest options income strategy. If you already hold Nifty (via ETF or futures), you sell a call option against it to collect regular income — essentially renting out the upside potential of your holding.

## The Income Math

Say you hold 1 lot of Nifty 50 (65 units, worth approximately ₹13 lakh at 20,000):

- Sell 20,500 CE for ₹85
- Per lot income: ₹85 × 65 = **₹5,525**
- Monthly (4 weekly expiries): ~₹22,100
- Annualized: ~₹2,65,200 (20% annualized on ₹13 lakh holding)

This assumes Nifty stays below 20,500 each week. If it crosses, your holding is "called away" at 20,500 — you still profit, just not above that level.

## Strike Selection for Covered Calls

**Conservative (income focus)**: Sell 1–2% OTM calls. Higher probability of keeping premium, lower income per trade.

**Aggressive (income + appreciation)**: Sell ATM or slightly OTM. Higher premium, but more often your upside gets capped.

**Standard approach**: Sell the strike at your short-term resistance level. You collect premium AND the trade aligns with your technical view.

## The Roll Strategy — What to Do When Nifty Rallies

If Nifty rallies toward your sold strike, you have options:

1. **Let it go**: Accept that your holding gets called away at the strike price. You made money on both the holding appreciation and the premium.

2. **Roll up and out**: Buy back the current call, sell a higher-strike call in the next expiry. You collect a net debit (costs something) but keep your upside open to a higher level.

3. **Close early**: If the call has lost 50-70% of its value (through time decay or price drop), close it and sell a new one. This is "rolling down the tent."

## Tax Implications in India

Options premium received is taxed as **business income** (not capital gains) under Indian tax law — this applies to systematic options selling strategies. Consult a CA who specializes in F&O taxation before starting a covered call program at scale.`,
  },
};

export default function StrategyGuide() {
  const { slug } = useParams();
  const strategy = STRATEGIES[slug];
  if (!strategy) return <Navigate to="/learn" replace />;

  const html = `<p class="txt-secondary text-sm leading-relaxed mb-4">${strategy.body
    .replace(/^## (.+)$/gm, '<h2 class="text-base font-section mt-8 mb-3 text-white border-b border-[var(--border)] pb-2">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/`([^`]+)`/g, "<code class='bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono txt-accent'>$1</code>")
    .replace(/^- (.+)$/gm, '<li class="txt-secondary text-sm ml-4 list-disc mb-1">$1</li>')
    .replace(/\n\n/g, '</p><p class="txt-secondary text-sm leading-relaxed mb-4">')
  }</p>`;

  const otherStrategies = Object.entries(STRATEGIES).filter(([s]) => s !== slug);

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-white">
      <SEOMeta
        title={strategy.title}
        description={strategy.description}
        url={`/strategy-guide/${slug}`}
        keywords={strategy.keywords}
      />
      <ExitIntent />

      <nav className="border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-section text-lg tracking-widest text-white hover:txt-accent transition-colors">
          ALGOFORGE
        </Link>
        <div className="flex items-center gap-6 text-xs font-section tracking-wider txt-muted">
          <Link to="/learn" className="hover:text-white transition-colors">LEARN</Link>
          <Link to="/blog" className="hover:text-white transition-colors">BLOG</Link>
          <Link to="/register" className="px-3 py-1.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent-muted)] transition-colors">START FREE</Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/learn" className="flex items-center gap-1.5 txt-muted text-xs font-section tracking-wider hover:text-white transition-colors mb-8">
          <ChevronLeft className="w-3.5 h-3.5" /> All Guides
        </Link>

        <div className="text-xs font-section tracking-wider txt-accent mb-2">{strategy.category}</div>
        <h1 className="font-section text-2xl text-white leading-snug mb-8">{strategy.title}</h1>

        {/* Strategy quick-facts */}
        <div className="panel p-5 mb-8 space-y-4">
          <div>
            <div className="overline text-xs mb-2">Strategy Legs</div>
            <ul className="space-y-1">
              {strategy.legs.map((leg, i) => (
                <li key={i} className="text-sm txt-secondary flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full border border-[var(--border)] flex items-center justify-center text-xs font-section txt-muted flex-shrink-0">{i + 1}</span>
                  {leg}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[var(--border)]">
            <div>
              <div className="overline text-xs mb-1 txt-accent">Max Profit</div>
              <div className="text-xs txt-secondary">{strategy.maxProfit}</div>
            </div>
            <div>
              <div className="overline text-xs mb-1 text-red-400">Max Loss</div>
              <div className="text-xs txt-secondary">{strategy.maxLoss}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="panel p-4 border-[var(--accent)]/20">
            <div className="overline text-xs mb-2 txt-accent">Use When</div>
            <ul className="space-y-1">
              {strategy.bestWhen.map((w, i) => <li key={i} className="text-xs txt-secondary flex gap-2"><span className="text-emerald-500 mt-0.5">✓</span>{w}</li>)}
            </ul>
          </div>
          <div className="panel p-4 border-red-500/20">
            <div className="overline text-xs mb-2 text-red-400">Avoid When</div>
            <ul className="space-y-1">
              {strategy.avoid.map((w, i) => <li key={i} className="text-xs txt-secondary flex gap-2"><span className="text-red-500 mt-0.5">✗</span>{w}</li>)}
            </ul>
          </div>
        </div>

        <div dangerouslySetInnerHTML={{ __html: html }} />

        {/* CTA */}
        <div className="mt-10 panel p-6 border-[var(--accent)]/20">
          <div className="font-section text-sm text-white mb-1">Run this strategy on paper first</div>
          <p className="txt-secondary text-xs mb-4">AlgoForge's payoff calculator + paper trading lets you test {strategy.title.split("—")[0].trim()} with ₹10 lakh virtual capital. Free, no card needed.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--accent)] hover:bg-indigo-500 text-white font-section tracking-wider text-xs transition-colors">
            Start Free <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* More strategies */}
        <div className="mt-10">
          <div className="overline text-xs mb-3">More Strategy Guides</div>
          <div className="space-y-1">
            {otherStrategies.map(([s, st]) => (
              <Link key={s} to={`/strategy-guide/${s}`}
                className="flex items-center justify-between group py-2.5 px-3 hover:bg-[var(--bg-surface)] border border-transparent hover:border-[var(--border)] transition-all">
                <span className="text-sm text-white group-hover:txt-accent transition-colors">{st.title.split("—")[0].trim()}</span>
                <ArrowRight className="w-4 h-4 txt-muted group-hover:text-white transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
